import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { GuessResult } from '../types/game';
import { StockCategoryFilter } from '../types/stockCategories';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';

interface SyncMeta {
  userId: string;
  gameDate: string;
  guessCount: number;
  lastSyncedAt: string;
  supabaseLatestGuessAt: string;
}

interface UseGuessHistorySyncOptions {
  category: StockCategoryFilter;
  gameDate: string; // YYYY-MM-DD
  localGuesses: GuessResult[];
  onSyncComplete?: (guesses: GuessResult[]) => void;
  isReplayMode?: boolean;
}

interface UseGuessHistorySyncResult {
  guesses: GuessResult[];
  isLoading: boolean;
  error: string | null;
}

// In-memory cooldown tracker: "category|date" -> timestamp
const cooldownMap = new Map<string, number>();
const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

function getCooldownKey(category: string, gameDate: string): string {
  return `${category}|${gameDate}`;
}

function isCooldownActive(category: string, gameDate: string): boolean {
  const key = getCooldownKey(category, gameDate);
  const lastFetch = cooldownMap.get(key);
  if (!lastFetch) return false;
  return Date.now() - lastFetch < COOLDOWN_MS;
}

function setCooldown(category: string, gameDate: string): void {
  cooldownMap.set(getCooldownKey(category, gameDate), Date.now());
}

function getSyncMeta(category: string): SyncMeta | null {
  try {
    const storageKey = 'wallstreetle_categories';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data[category]?.syncMeta ?? null;
  } catch {
    return null;
  }
}

function saveSyncMeta(category: string, meta: SyncMeta): void {
  try {
    const storageKey = 'wallstreetle_categories';
    const raw = localStorage.getItem(storageKey);
    const data = raw ? JSON.parse(raw) : {};
    if (!data[category]) data[category] = {};
    data[category].syncMeta = meta;
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Hook for hybrid guess history sync (localStorage + Supabase).
 *
 * 1. Renders instantly from localGuesses (no loading gate)
 * 2. If logged in and local data is missing/stale, does a 2-step sync:
 *    - Step A: cheap `get_guess_summary` RPC to check freshness
 *    - Step B: full fetch from `game_tracking` only if needed
 * 3. Calls onSyncComplete with real guesses when sync finishes
 */
export function useGuessHistorySync({
  category,
  gameDate,
  localGuesses,
  onSyncComplete,
  isReplayMode = false
}: UseGuessHistorySyncOptions): UseGuessHistorySyncResult {
  const { user } = useAuth();
  const [supabaseGuesses, setSupabaseGuesses] = useState<GuessResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasSynced = useRef(false);
  const supabaseGuessesDateRef = useRef('');
  const onSyncCompleteRef = useRef(onSyncComplete);
  onSyncCompleteRef.current = onSyncComplete;

  // Reset when category/date changes
  useEffect(() => {
    hasSynced.current = false;
    setSupabaseGuesses([]);
    setError(null);
  }, [category, gameDate]);

  const runSync = useCallback(async () => {
    if (!user) return;
    if (hasSynced.current) return;
    if (isCooldownActive(category, gameDate)) return;

    // Prevent cross-day data leaks: never fetch data for a stale gameDate
    if (!isReplayMode) {
      const today = getCurrentGameTimestamp();
      if (gameDate !== today) return;
    }

    // If local guesses exist and have real data (not placeholders), check syncMeta
    const hasRealLocalGuesses = localGuesses.length > 0 &&
      localGuesses.some(g => g.company !== '(Previous guess)');

    if (hasRealLocalGuesses) {
      const meta = getSyncMeta(category);
      if (meta && meta.userId === user.id && meta.gameDate === gameDate) {
        // SyncMeta matches — local is fresh, skip
        hasSynced.current = true;
        return;
      }
    }

    // Step A: cheap summary check
    setIsLoading(true);
    setError(null);

    try {
      const { data: summaryData, error: summaryError } = await supabase
        .rpc('get_guess_summary', {
          p_user_id: user.id,
          p_category: category,
          p_game_date: gameDate
        });

      if (summaryError) {
        console.error('get_guess_summary error:', summaryError);
        setError('Failed to check guess history');
        hasSynced.current = true;
        setCooldown(category, gameDate);
        return;
      }

      const summary = summaryData?.[0];
      const serverGuessCount = summary?.guess_count ?? 0;
      const serverLatestGuessAt = summary?.latest_guessed_at ?? '';

      // No server data — nothing to sync
      if (serverGuessCount === 0) {
        hasSynced.current = true;
        setCooldown(category, gameDate);
        return;
      }

      // Check if local is already up-to-date
      const meta = getSyncMeta(category);
      if (
        hasRealLocalGuesses &&
        meta &&
        meta.userId === user.id &&
        meta.gameDate === gameDate &&
        meta.guessCount === serverGuessCount &&
        meta.supabaseLatestGuessAt === serverLatestGuessAt
      ) {
        hasSynced.current = true;
        setCooldown(category, gameDate);
        return;
      }

      // Step B: full fetch needed
      const { data: rows, error: fetchError } = await supabase
        .from('game_tracking')
        .select('guess_text, is_correct, guessed_at, market_cap_difference, market_cap_percentage, market_cap_direction, attempt_number')
        .eq('user_id', user.id)
        .eq('category', category)
        .eq('game_date', gameDate)
        .order('attempt_number', { ascending: true });

      if (fetchError) {
        console.error('Failed to fetch guess history:', fetchError);
        setError('Failed to load guess history');
        hasSynced.current = true;
        setCooldown(category, gameDate);
        return;
      }

      const fetchedGuesses: GuessResult[] = (rows || []).map(row => ({
        company: row.guess_text,
        isCorrect: row.is_correct,
        timestamp: new Date(row.guessed_at),
        marketCapDiff: row.market_cap_difference ? {
          difference: row.market_cap_difference,
          percentage: row.market_cap_percentage!,
          direction: row.market_cap_direction as 'higher' | 'lower'
        } : undefined,
        attemptNumber: row.attempt_number
      }));

      setSupabaseGuesses(fetchedGuesses);
      supabaseGuessesDateRef.current = gameDate;

      // Save syncMeta
      saveSyncMeta(category, {
        userId: user.id,
        gameDate,
        guessCount: serverGuessCount,
        lastSyncedAt: new Date().toISOString(),
        supabaseLatestGuessAt: serverLatestGuessAt
      });

      hasSynced.current = true;
      setCooldown(category, gameDate);

      // Notify consumer
      if (onSyncCompleteRef.current && fetchedGuesses.length > 0) {
        onSyncCompleteRef.current(fetchedGuesses);
      }
    } catch (err) {
      console.error('Error syncing guess history:', err);
      setError('Failed to sync guess history');
      hasSynced.current = true;
      setCooldown(category, gameDate);
    } finally {
      setIsLoading(false);
    }
  }, [user, category, gameDate, localGuesses, isReplayMode]);

  useEffect(() => {
    runSync();
  }, [runSync]);

  // Merge: prefer local if real, otherwise use supabase (only if dates match)
  const guesses = useMemo(() => {
    if (!user) return localGuesses;

    const hasRealLocal = localGuesses.length > 0 &&
      localGuesses.some(g => g.company !== '(Previous guess)');

    if (hasRealLocal) return localGuesses;
    if (supabaseGuesses.length > 0 && supabaseGuessesDateRef.current === gameDate) {
      return supabaseGuesses;
    }
    return localGuesses;
  }, [user, localGuesses, supabaseGuesses, gameDate]);

  return { guesses, isLoading, error };
}
